/**
 * Entry pre-step (web): the brief is typed into the app's own hero composer
 * rather than a bare bordered textarea, so posting a job starts in the same
 * input employers already use everywhere else — "+" attaches a job post
 * (PDF/Word/photo) and pours its text into the brief, the mic dictates, and
 * the arrow builds the draft. Native keeps the plain card in the shared
 * sibling: `NovaComposer` is web-only (Web Speech API, CSS classes).
 *
 * The AI "Build Job Post" button lives on native only: here the composer's own
 * send arrow (and the wizard's top-right Next) already run generation, and a
 * third copy of the same action only added dead height to the dialog.
 */
import { useEffect, useRef, type ChangeEvent } from 'react';
import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import { spacing } from '@tabbio/tokens';
import { useTheme } from '../../../hooks/useTheme';
import { useHaptics } from '../../../hooks/useHaptics';
import { Alert } from '../../../lib/alert.web';
import { NativeButton } from '../../common/NativeButton';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { NovaComposer } from '../../nova/NovaComposer.web';
import {
  JOB_POST_ATTACHMENT_ACCEPT,
  useJobPostAttachment,
} from '../../cv/create/use-job-post-attachment';
import { InlineError } from './post-job-ui';
import type { PostJobFlowController } from './use-post-job-flow';

export function PostJobEntryStep({ flow }: { flow: PostJobFlowController }) {
  const { t } = useTranslation('common');
  const { theme } = useTheme();
  const haptics = useHaptics();
  const briefRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { state, setEntry, errorFor, focusFieldId, clearFocusField } = flow;

  const setBrief = (text: string) => {
    const mode = text.trim()
      ? ('ai' as const)
      : state.entry.mode === 'ai'
        ? null
        : state.entry.mode;
    setEntry({ brief: text, mode });
  };

  const attachment = useJobPostAttachment({
    jobDescription: state.entry.brief,
    onJobDescriptionChange: setBrief,
    onError: (title, message) => Alert.alert(title, message),
  });

  useEffect(() => {
    if (focusFieldId === 'brief') {
      briefRef.current?.focus();
      clearFocusField();
    }
  }, [clearFocusField, focusFieldId]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    event.currentTarget.value = '';
    if (!selected) return;
    void attachment.attach({
      name: selected.name,
      type: selected.type || 'application/octet-stream',
      file: selected,
      size: selected.size,
    });
  };

  return (
    <View style={styles.wrap}>
      <NovaComposer
        inputRef={briefRef}
        value={state.entry.brief}
        onChange={setBrief}
        status={
          flow.isGenerating || attachment.isExtracting ? 'compiling' : 'idle'
        }
        onSubmit={() => void flow.generateFromBrief()}
        onOpenBuilder={() => {
          if (attachment.isExtracting) return;
          fileInputRef.current?.click();
        }}
        placeholder={t('jobPost.step1.textareaPlaceholder')}
        addLabel={t('jobPost.step1.attach')}
        busyLabel={t('jobPost.step1.attaching')}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept={JOB_POST_ATTACHMENT_ACCEPT}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {attachment.fileName ? (
        <View style={styles.chipRow}>
          <span className="tabbio-composer-tag" dir="auto">
            <MaterialSymbol name="description" />
            {attachment.fileName}
          </span>
          <button
            type="button"
            onClick={attachment.clear}
            aria-label={t('jobPost.step1.attachClear')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: 2,
              border: 0,
              background: 'none',
              cursor: 'pointer',
              color: theme.textTertiary,
            }}
          >
            <MaterialSymbol name="close" size={14} />
          </button>
        </View>
      ) : null}

      <InlineError messageKey={errorFor('brief')} />

      <View style={styles.orRow}>
        <View style={[styles.orLine, { backgroundColor: theme.borderSubtle }]} />
        <Text style={[styles.orText, { color: theme.textTertiary }]}>
          {t('jobPost.step1.or')}
        </Text>
        <View style={[styles.orLine, { backgroundColor: theme.borderSubtle }]} />
      </View>

      <NativeButton
        title={t('jobPost.actions.postManually')}
        onPress={() => {
          haptics.trigger('selection');
          flow.startManually();
        }}
        variant="outline"
        size="lg"
        pill
        block
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing[5],
  },
  chipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[1],
  },
  orRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing[3],
  },
  orLine: {
    flex: 1,
    height: StyleSheet.hairlineWidth,
  },
  orText: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
});
